import React, { useState, useEffect, useMemo } from 'react';
import { X, Copy, Check, MessageCircle, CheckCircle2, ListOrdered } from 'lucide-react';
import {
  generateDiscordTable,
  DISCORD_DEFAULT_MESSAGE_LIMIT,
  DISCORD_NITRO_MESSAGE_LIMIT
} from '../utils/exportTable';

const CHUNK_SEPARATOR = '\n\n--- paste the above as a separate Discord message ---\n\n';

const LIMIT_OPTIONS = [
  { value: DISCORD_DEFAULT_MESSAGE_LIMIT, label: 'Free', detail: '2,000 chars' },
  { value: DISCORD_NITRO_MESSAGE_LIMIT, label: 'Nitro', detail: '4,000 chars' }
];

/**
 * Discord Copy Modal
 *
 * Lets the user pick a Discord message character limit (Free 2000 / Nitro 4000),
 * regenerates the compact Discord export for that limit, and presents each
 * resulting message as its own card with an individual copy button.
 */
export default function DiscordCopyModal({
  isOpen,
  onClose,
  protocols = [],
  options = {}
}) {
  const [messageLimit, setMessageLimit] = useState(DISCORD_DEFAULT_MESSAGE_LIMIT);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const { status, categories, sortOption } = options;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMessageLimit(DISCORD_DEFAULT_MESSAGE_LIMIT);
      setCopiedIndex(null);
    }
  }, [isOpen]);

  const chunks = useMemo(() => {
    if (!isOpen) return [];
    const raw = generateDiscordTable(protocols, { status, categories, sortOption, messageLimit });
    return raw
      .split(CHUNK_SEPARATOR)
      .map((chunk) => chunk.trim())
      .filter(Boolean);
  }, [isOpen, protocols, status, categories, sortOption, messageLimit]);

  if (!isOpen) return null;

  const isSingleChunk = chunks.length <= 1;

  const handleCopyChunk = async (chunk, index) => {
    try {
      await navigator.clipboard.writeText(chunk);
      setCopiedIndex(index);
      setTimeout(() => {
        setCopiedIndex((prev) => (prev === index ? null : prev));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 bg-slate-950/85 backdrop-blur-md animate-fadeIn overflow-y-auto touch-pan-y">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full flex flex-col max-h-[92vh] sm:max-h-[88vh] shadow-2xl relative my-auto overflow-hidden">

        {/* Discord blurple accent line */}
        <div className="h-0.5 w-full bg-gradient-to-r from-[#5865F2] via-[#5865F2]/50 to-transparent shrink-0" />

        {/* Sticky Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-800 shrink-0 bg-slate-900">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-[#5865F2]/15 text-[#8B9CFF] border border-[#5865F2]/30">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">Copy for Discord</h2>
              <p className="text-[11px] sm:text-xs text-slate-400">
                Your stack, split into ready-to-paste Discord messages
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 touch-pan-y space-y-4 text-xs">

          {/* Message Limit Selector */}
          <div className="bg-slate-950/60 p-3.5 sm:p-4 rounded-xl border border-slate-800 space-y-2.5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
              <label className="block text-[11px] font-semibold text-slate-400 shrink-0">
                Discord Message Limit
              </label>
              <div className="flex rounded-lg bg-slate-900 p-0.5 border border-slate-800 gap-0.5 w-full sm:w-auto">
                {LIMIT_OPTIONS.map(({ value, label, detail }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMessageLimit(value)}
                    className={`flex-1 sm:flex-none py-1.5 px-3 text-[11px] font-semibold rounded-md transition text-center whitespace-nowrap ${
                      messageLimit === value
                        ? 'bg-[#5865F2] text-white shadow-sm'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {label} <span className={messageLimit === value ? 'text-white/75' : 'text-slate-500'}>· {detail}</span>
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Free Discord accounts can send up to 2,000 characters per message. Have Discord Nitro?
              Switch to 4,000 characters to fit more of your stack into each message.
            </p>
          </div>

          {/* Fit status message */}
          {isSingleChunk ? (
            <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs flex items-center space-x-2 animate-fadeIn">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span className="font-medium">
                Nice — your whole stack fits in a single Discord message. Just copy and paste it.
              </span>
            </div>
          ) : (
            <div className="p-2.5 rounded-xl bg-[#5865F2]/10 border border-[#5865F2]/25 text-[#B9C1FF] text-xs flex items-center space-x-2 animate-fadeIn">
              <ListOrdered className="w-4 h-4 text-[#8B9CFF] flex-shrink-0" />
              <span className="font-medium">
                Your stack is split into {chunks.length} messages. Copy and paste each one into Discord, in order.
              </span>
            </div>
          )}

          {/* Message Cards */}
          <div className="space-y-3">
            {chunks.map((chunk, index) => {
              const isCopied = copiedIndex === index;
              return (
                <div
                  key={index}
                  className="rounded-xl border border-slate-800 bg-slate-950/60 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-slate-800 bg-slate-900/60">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="px-2 py-0.5 rounded-md bg-[#5865F2]/15 border border-[#5865F2]/30 text-[#A5B0FF] text-[10px] font-bold tracking-wide whitespace-nowrap">
                        {isSingleChunk ? 'Discord Message' : `Discord Message ${index + 1} of ${chunks.length}`}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono whitespace-nowrap">
                        {chunk.length.toLocaleString()} / {messageLimit.toLocaleString()} chars
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopyChunk(chunk, index)}
                      className={`shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold flex items-center space-x-1.5 transition border shadow-sm ${
                        isCopied
                          ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                          : 'bg-[#5865F2] hover:bg-[#4752C4] border-[#5865F2] text-white'
                      }`}
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Chunk Preview */}
                  <pre className="w-full p-3.5 text-slate-300 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words select-all max-h-56 overflow-y-auto overscroll-contain">
                    {chunk}
                  </pre>
                </div>
              );
            })}
          </div>

        </div>

        {/* Sticky Footer */}
        <div className="p-4 sm:p-5 border-t border-slate-800 flex items-center justify-between gap-3 shrink-0 bg-slate-900/95 backdrop-blur-sm">
          <div className="text-[11px] text-slate-500 hidden sm:block">
            Each message stays within the selected character limit
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition text-xs ml-auto"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
